<script setup lang="ts">
import SidebarName from '@/vue/molecules/sidebar/sidebar-name.vue';
import ErrorBox from '@/vue/molecules/error-box.vue';
import LazyImage from '@/vue/atoms/lazy-image.vue';
import ReccoMediaTypeAndDuration from '@/vue/molecules/recco/recco-media-type-and-duration.vue';
import {useI18n} from 'vue-i18n';
import {useBookmarksStore} from '@/ts/store/bookmarks-store';
import {useBookmarks} from '@/ts/composables/stateful/use-bookmarks';
import {computed, onMounted} from 'vue';
import {calculateMediaUrl} from '@/ts/utils/pure-functions';
import {useRoute} from 'vue-router';
import type {UuidDTO} from '@/ts/types/dto/sign-in-dto';
import type {ComponentContent} from '@/ts/types/dto/interventions-dto';
import {ReccoRecommendationTypeDTO} from '@/ts/types/dto/recco/recommendation.dto';

const {t: $t} = useI18n();
const bookmarksStore = useBookmarksStore();
const {fetchBookmarks} = useBookmarks();
const route = useRoute();

// Image dimensions for bookmark thumbnails
const IMAGE_WIDTH = 250;
const IMAGE_HEIGHT = 160;
const imageAspectRatio = `${IMAGE_WIDTH} / ${IMAGE_HEIGHT}`;

// Check if we're already viewing content - if so, use replace to avoid stacking
const isViewingContent = computed(() => {
  return route.name === 'block-content';
});

// Build link for bookmark - preserve state when replacing
function getBookmarkLink(bookmarkId: UuidDTO): {name: string; params: {itemId: UuidDTO}; state?: {backLabel: string}} {
  const link: {name: string; params: {itemId: UuidDTO}; state?: {backLabel: string}} = {
    name: 'block-content',
    params: {itemId: bookmarkId},
  };

  // Only set state when NOT replacing (first navigation from sidebar)
  if (!isViewingContent.value) {
    link.state = {backLabel: 'back'};
  }

  return link;
}

function getContentType(bookmark: ComponentContent): ReccoRecommendationTypeDTO {
  if (bookmark.video) {
    return ReccoRecommendationTypeDTO.Video;
  }
  if (bookmark.audio) {
    return ReccoRecommendationTypeDTO.Audio;
  }
  return ReccoRecommendationTypeDTO.Article;
}

onMounted(() => {
  void fetchBookmarks();
});
</script>


<template>
  <div>
    <sidebar-name :name="$t('bookmarks')"/>

    <!-- Skeleton loading state -->
    <div v-if="bookmarksStore.isLoading">
      <ul class="bookmarks-list" aria-hidden="true">
        <li v-for="n in 4" :key="n" class="bookmark-item">
          <q-skeleton class="skeleton-image"/>
          <div class="bookmark-content">
            <q-skeleton type="text" width="40%"/>
            <q-skeleton type="text" width="90%"/>
            <q-skeleton type="text" width="90%"/>
          </div>
        </li>
      </ul>
    </div>

    <!-- Error state -->
    <error-box v-else-if="bookmarksStore.error"/>

    <!-- Empty state -->
    <div v-else-if="bookmarksStore.bookmarks.length === 0" class="empty">
      {{ $t('no_bookmarks') }}
    </div>

    <!-- Content -->
    <ul v-else class="bookmarks-list" :aria-label="$t('bookmarks')">
      <li
        v-for="bookmark in bookmarksStore.bookmarks"
        :key="bookmark.id"
        class="bookmark-item"
      >
        <router-link
          class="focus-visible router-link"
          :to="getBookmarkLink(bookmark.id)"
          :replace="isViewingContent"
        >
          <div v-if="bookmark.image" class="image-container">
            <lazy-image
              :src="calculateMediaUrl(bookmark.image)"
              :alt-txt="bookmark.name || ''"
              :width="IMAGE_WIDTH"
              :height="IMAGE_HEIGHT"
            />
            <div v-if="bookmark.audio || bookmark.video" class="play-button" aria-hidden="true">
              <img src="@/assets/img/play-icon-light.svg" alt="" class="play-icon"/>
            </div>
          </div>
          <div class="bookmark-content">
            <recco-media-type-and-duration :type="getContentType(bookmark)" :duration="bookmark.duration"/>
            <div class="bookmark-title">
              {{ bookmark.name }}
            </div>
          </div>
        </router-link>
      </li>
    </ul>
  </div>
</template>

<style scoped lang="scss">
.router-link {
  display: block;
  border-radius: var(--card-border-radius);
}

.bookmarks-list {
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  gap: var(--Spacing-spacing-4);
  list-style: none;
  padding: 0;
}

.bookmark-item {
  gap: var(--Spacing-spacing-4);
  background: var(--Color-neutral-10);
  border-radius: var(--Border-radius-medium);
  width: 100%;
  min-width: 150px;
  max-width: 215px;
}

.image-container {
  position: relative;
  border-radius: var(--card-border-radius);
  overflow: hidden;
  background-color: var(--Neutral-UI-neutral-ui-20);
}

.play-button {
  position: absolute;
  top: var(--Spacing-spacing-3);
  left: var(--Spacing-spacing-3);
  width: 32px;
  height: 32px;
  background-color: rgb(0 0 0 / 50%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
}

.play-icon {
  width: 100%;
  height: 100%;
  margin-left: 1px;
}

.bookmark-content {
  padding: var(--Spacing-spacing-3) 0 var(--Spacing-spacing-4);
  display: flex;
  flex-direction: column;
}

.bookmark-title {
  @include font-body-sm-bold;
}

.empty {
  text-align: center;
  padding: 16px;
}

.skeleton-image {
  aspect-ratio: v-bind(imageAspectRatio);
  border-radius: var(--card-border-radius);
}
</style>
